<template>
  <div>
    <refund-type-step
      v-if="isRefundTypeStep"
      :initialValues="refundTypeValues"
      :initialChargeSplit="split"
      :totalAmount="totalAmount"
      @submit="handleRefundTypeSubmit"
      @update:isInvalid="handleRefundTypeInvalid"
    >
      <cancel-ok-action-buttons-steps
        :boletoCancelStep="boletoCancelStep"
        :disabled="invalid.refundTypeStep"
        @cancel="closeModal"
      />
    </refund-type-step>

    <bank-details-step
      v-else-if="isBankDetailsStep"
      :initialValues="bankValues"
      @submit="handleBankDetailsSubmit"
      @update:isInvalid="handleBankDetailsInvalid"
    >
      <cancel-ok-action-buttons-steps
        :boletoCancelStep="boletoCancelStep"
        :disabled="invalid.bankDetailsStep"
        @cancel="goToStep(BOLETO_CANCEL_STEPS_ENUM.REFUND_TYPE)"
      />
    </bank-details-step>

    <confirm-bank-details-step
      v-else-if="isConfirmBankDetailsStep"
      :passwordIncorrect="passwordIncorrect"
      :initialValues="bankDetailsValues"
      :recipient="getRecipientNameBasedOnSplitId"
      @submit="handleConfirmBankDetails"
      @edit="goToStep(BOLETO_CANCEL_STEPS_ENUM.REFUND_TYPE)"
      @update:isInvalid="handleConfirmBankDetailsInvalid"
    >
      <cancel-ok-action-buttons-steps
        :boletoCancelStep="boletoCancelStep"
        :disabled="loadingSubmit"
        @cancel="goToStep(BOLETO_CANCEL_STEPS_ENUM.BANK_DETAILS)"
      />
    </confirm-bank-details-step>

    <error-step
      v-else-if="isErrorStep"
      :error="error"
      @retry="goToStep(BOLETO_CANCEL_STEPS_ENUM.BANK_DETAILS)"
    />
  </div>
</template>
<script>
import { mapActions } from 'vuex'

import { BOLETO_CANCEL_ERRORS_ENUM } from '@/enums/boleto-cancel-errors.enum'

import {
  BOLETO_CANCEL_STEPS_ENUM,
  isBankDetailsStep,
  isErrorStep,
  isRefundTypeStep,
  isConfirmBankDetailsStep
} from './enums/steps.enum'

import BankDetailsStep from './steps/BankDetailsStep.vue'
import RefundTypeStep from './steps/RefundTypeStep.vue'
import ConfirmBankDetailsStep from './steps/ConfirmBankDetailsStep.vue'
import ErrorStep from './steps/ErrorStep.vue'

import CancelOkActionButtonsSteps from './components/CancelOkActionButtonsSteps.vue'

import BoletoCancelContainerManager from './boleto-cancel-container.manager.js'
import BoletoCancelErrorManager from './boleto-cancel-error.manager'

import { REFUND_TYPE_ENUM } from './enums/refund-type.enum'
import { SPLIT_ENUM } from './enums/splits.enum'

export default {
  name: 'BoletoCancel',
  components: {
    RefundTypeStep,
    BankDetailsStep,
    ConfirmBankDetailsStep,
    ErrorStep,
    CancelOkActionButtonsSteps
  },
  props: {
    totalAmount: Number,
    split: {
      type: Array,
      required: false,
      default: () => []
    },
    chargeId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      currentStep: BOLETO_CANCEL_STEPS_ENUM.REFUND_TYPE,
      refundTypeValues: {
        refundType: REFUND_TYPE_ENUM.TOTAL,
        amount: 0,
        totalAmount: 0,
        split: SPLIT_ENUM.ALL
      },
      invalid: {
        refundTypeStep: false,
        bankDetailsStep: false,
        confirmBankDetailsStep: false
      },
      bankValues: {
        holderName: '',
        holderDocument: '',
        bank: '',
        branchNumber: '',
        branchCheckDigit: '',
        accountNumber: '',
        accountCheckDigit: '',
        type: ''
      },
      passwordIncorrect: false,
      error: BOLETO_CANCEL_ERRORS_ENUM.GENERIC,
      loadingSubmit: false
    }
  },
  computed: {
    BOLETO_CANCEL_STEPS_ENUM () {
      return BOLETO_CANCEL_STEPS_ENUM
    },
    boletoCancelStep () {
      return this.currentStep.toLowerCase()
    },
    isRefundTypeStep () {
      return isRefundTypeStep(this.currentStep)
    },
    isBankDetailsStep () {
      return isBankDetailsStep(this.currentStep)
    },
    isConfirmBankDetailsStep () {
      return isConfirmBankDetailsStep(this.currentStep)
    },
    isErrorStep () {
      return isErrorStep(this.currentStep)
    },
    // The bank details has 2 extra fields to show: totalValue and amount
    bankDetailsValues () {
      return {
        // We join the bank form with the extra fields
        ...this.bankValues,
        amount: this.refundTypeValues.amount,
        totalAmount: this.totalAmount
      }
    },
    getRecipientNameBasedOnSplitId () {
      if (!this.refundTypeValues.split) return

      return new BoletoCancelContainerManager({
        initialChargeSplit: this.split
      }).getRecipientNameBasedOnSplitId(this.refundTypeValues.split)
    }
  },
  created () {
    this.refundTypeValues.split = this.split
    this.refundTypeValues.totalAmount = this.totalAmount
  },
  methods: {
    ...mapActions(['CANCEL_CHARGE']),
    goToStep (step) {
      this.currentStep = step
    },
    handleRefundTypeSubmit (values) {
      this.refundTypeValues = values
      this.goToStep(BOLETO_CANCEL_STEPS_ENUM.BANK_DETAILS)
    },
    handleBankDetailsSubmit (values) {
      this.bankValues = values
      this.goToStep(BOLETO_CANCEL_STEPS_ENUM.CONFIRM_BANK_DETAILS)
    },
    async handleConfirmBankDetails (password) {
      if (!password) {
        this.passwordIncorrect = true
        return
      }

      this.passwordIncorrect = false

      const data = new BoletoCancelContainerManager({
        refundTypeValues: this.refundTypeValues,
        bankDetailsValues: this.bankDetailsValues,
        initialChargeSplit: this.split
      }).prepareDataForSubmission(password)

      await this.submit(data)
    },
    async submit (values) {
      this.loadingSubmit = true
      try {
        await this.CANCEL_CHARGE({ body: values, chargeId: this.chargeId })
        this.$emit('success')
        this.closeModal()
      } catch (error) {
        if (new BoletoCancelErrorManager(error).isPasswordIncorrectError()) {
          this.passwordIncorrect = true
          return
        }

        this.error = new BoletoCancelErrorManager(
          error
        ).getBoletoCancelErrorBasedOnResponse()

        this.goToStep(BOLETO_CANCEL_STEPS_ENUM.ERROR)
      } finally {
        this.loadingSubmit = false
      }
    },
    handleBankDetailsInvalid (value) {
      this.invalid.bankDetailsStep = value
    },
    handleRefundTypeInvalid (value) {
      this.invalid.refundTypeStep = value
    },
    handleConfirmBankDetailsInvalid (value) {
      this.invalid.confirmBankDetailsStep = value
    },
    reset () {
      this.currentStep = BOLETO_CANCEL_STEPS_ENUM.REFUND_TYPE
      this.refundTypeValues = {
        refundType: REFUND_TYPE_ENUM.TOTAL,
        amount: 0,
        totalAmount: 0,
        split: SPLIT_ENUM.ALL
      }

      this.invalid = {
        refundTypeStep: false,
        bankDetailsStep: false,
        confirmBankDetailsStep: false
      }
      this.bankValues = {
        holderName: '',
        holderDocument: '',
        bank: '',
        branchNumber: '',
        branchCheckDigit: '',
        accountNumber: '',
        accountCheckDigit: '',
        type: ''
      }
      this.passwordIncorrect = false
      this.error = BOLETO_CANCEL_ERRORS_ENUM.GENERIC
      this.loadingSubmit = false
    },
    closeModal () {
      this.reset()
      this.$emit('close')
    }
  }
}
</script>
